'use client';

import { useState, useEffect } from 'react';

import Icon from '@/components/ui/AppIcon';
import UnifiedSearchWidget from './UnifiedSearchWidget';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className={`relative overflow-hidden bg-gradient-to-br from-primary via-[#0052A3] to-secondary pt-24 pb-32 ${className}`}>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              AI-Powered Travel Planning
            </h1>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`relative overflow-hidden bg-gradient-to-br from-primary via-[#0052A3] to-secondary pt-24 pb-32 ${className}`}>
      {/* Simplified Background Elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-accent/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-blue/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-white mb-12">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8 animate-fade-in">
            <Icon name="SparklesIcon" size={20} className="text-accent" />
            <span className="text-sm font-black">AI-Powered • UK Focused • GBP Pricing</span>
          </div>

          {/* Main Heading */}
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight animate-fade-in">
            <span className="inline-block px-4 py-2 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              Smart Travel Planning with <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-[#FF8C5A]">AI Innovation</span>
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto text-white/90 font-bold animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Automated flight + hotel planning with real-time AI recommendations
          </p>
          
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto text-white/80 font-semibold animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Tailored for UK customers • Compare thousands of options • Book with confidence
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <button className="px-8 py-4 bg-gradient-to-r from-accent to-[#FF8C5A] text-white text-lg font-heading font-black rounded-lg shadow-lg border-2 border-accent transition-all duration-300 hover:scale-105 hover:shadow-xl">
              Plan Your Trip Now
            </button>
            <button className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-heading font-black rounded-lg border-2 border-white/30 transition-all duration-300 hover:bg-white/20 hover:scale-105">
              Get AI Recommendations
            </button>
            <a
              href="tel:02046152876"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-trust-green text-white text-lg font-heading font-black rounded-lg shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:bg-emerald-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path fillRule="evenodd" d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z" clipRule="evenodd" />
              </svg>
              020 4615 2876
            </a>
          </div>

          {/* Unified Search Widget */}
          <UnifiedSearchWidget />
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mt-20 animate-fade-in" style={{ animationDelay: '0.5s' }}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl mb-3 border border-white/20">
              <Icon name="SparklesIcon" size={28} className="text-accent" />
            </div>
            <div className="text-3xl font-heading font-black text-white mb-1">AI-Powered</div>
            <div className="text-sm text-white/70 font-bold">Smart Recommendations</div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl mb-3 border border-white/20">
              <Icon name="ClockIcon" size={28} className="text-sky-blue" />
            </div>
            <div className="text-3xl font-heading font-black text-white mb-1">24/7</div>
            <div className="text-sm text-white/70 font-bold">Automated Support</div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl mb-3 border border-white/20">
              <Icon name="CurrencyPoundIcon" size={28} className="text-trust-green" />
            </div>
            <div className="text-3xl font-heading font-black text-white mb-1">GBP</div>
            <div className="text-sm text-white/70 font-bold">UK-Focused Pricing</div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl mb-3 border border-white/20">
              <Icon name="UserGroupIcon" size={28} className="text-coral" />
            </div>
            <div className="text-3xl font-heading font-black text-white mb-1">50K+</div>
            <div className="text-sm text-white/70 font-bold">UK Travelers</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;